import { PendingButton } from "@/components/common/PendingButton/PendingButton"
import { toast } from "@/components/common/Toast"
import { Badge } from "@/components/ui/badge"
import { Typography } from "@/components/ui/typography"
import {
  getApiErrorCode,
  getApiErrorDetail,
  toastApiError,
} from "@/features/material-compliance/api/apiError"
import {
  useRegulationSettings,
  useSaveRegulationSettings,
  type RegulationSettingRow,
  type RegulationSubscriptionIn,
} from "@/features/material-compliance/api/regulationSettingsApi"
import {
  normalizeFrameworkCode,
  regulationLabel,
} from "@/features/material-compliance/utils/regulationLabel"
import {
  initialRegulationSelection,
  toggleRegulationSelection,
  withRequiredRegulations,
  type SelectionEntry,
  type SelectionMap,
} from "@/features/material-compliance/utils/regulationSelection"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { Link2 } from "lucide-react"
import { useState, type ReactNode } from "react"
import {
  SettingsList,
  SettingsListGroup,
  SettingsListRow,
  SettingsSwitch,
} from "../../components/SettingsList"
import { SettingsPage, SettingsSection } from "../../components/SettingsPage"
import {
  SettingsLoadError,
  SettingsSkeleton,
} from "../../components/SettingsStates"
import { useUnsavedChanges } from "../../components/UnsavedChanges"

type Region = "eu" | "cn" | "us" | "other"

const REGION_ORDER: Region[] = ["eu", "cn", "us", "other"]

const REGION_LABEL_KEY: Record<Region, TranslationKeys> = {
  eu: "MC_ORG_REG_REGION_EU",
  cn: "MC_ORG_REG_REGION_CN",
  us: "MC_ORG_REG_REGION_US",
  other: "MC_ORG_REG_REGION_OTHER",
}

/** Groups by mc_regulations.jurisdiction (EU/EEA, CN, US/US-CA). */
function regionOf(row: RegulationSettingRow): Region {
  const codes = new Set(row.jurisdiction ?? [])
  if (codes.has("EU") || codes.has("EEA")) return "eu"
  if (codes.has("CN")) return "cn"
  if (codes.has("US") || codes.has("US-CA")) return "us"
  return "other"
}

/** Order-independent fingerprint of a selection (id + reason), for dirty
 *  checks. requiredSince is not user-editable, so it is left out on purpose:
 *  it never changes under the user's own edits. */
function selectionFingerprint(map: SelectionMap): string {
  return Object.keys(map)
    .sort()
    .map((id) => `${id}:${map[id].reason}`)
    .join("|")
}

function unavailableTooltipKey(
  reason: RegulationSettingRow["notPickableReason"],
): TranslationKeys {
  switch (reason) {
    case "no_copy":
      return "MC_ORG_REG_UNAVAILABLE_TOOLTIP_COPY"
    case "no_regulation_row":
      return "MC_ORG_REG_UNAVAILABLE_TOOLTIP_ROW"
    default:
      return "MC_ORG_REG_UNAVAILABLE_TOOLTIP"
  }
}

/** Toast copy for a refused save, naming the regulation it is about. The
 *  error carries ids and names this page sent, so they resolve against its
 *  own rows; anything unresolvable falls back to the generic save error. */
function regulationSaveError(
  error: unknown,
  rows: RegulationSettingRow[],
  selection: SelectionMap,
  translate: (key: TranslationKeys) => string,
): { key: TranslationKeys; values?: Record<string, string> } {
  const detail = getApiErrorDetail(error)
  const labelOf = (name: string) =>
    regulationLabel(normalizeFrameworkCode(name), name, translate)
  const named =
    typeof detail?.regulation === "string"
      ? detail.regulation
      : rows.find((r) => r.regulationId === detail?.regulation_id)?.name
  if (!named) return { key: "MC_TPL_SAVE_ERR" }
  const name = labelOf(named)

  switch (getApiErrorCode(error)) {
    case "REGULATION_NOT_SUPPORTED":
      return { key: "MC_ORG_REG_ERR_NOT_SUPPORTED", values: { name } }
    case "REGULATION_NOT_PICKABLE": {
      // Usually not picked by hand: switching on a regulation that needs it
      // selected it too (PFAS pulls in REACH Annex XVII).
      const dependents = rows
        .filter(
          (r) =>
            r.regulationId &&
            selection[r.regulationId] &&
            r.requires.includes(named),
        )
        .map((r) => labelOf(r.name))
      return dependents.length > 0
        ? {
            key: "MC_ORG_REG_ERR_NOT_PICKABLE_REQUIRED",
            values: { name, dependents: dependents.join(", ") },
          }
        : { key: "MC_ORG_REG_ERR_NOT_PICKABLE", values: { name } }
    }
    case "DEPENDENCY_MISSING": {
      const requires = Array.isArray(detail?.requires)
        ? detail.requires.filter((n): n is string => typeof n === "string")
        : []
      if (requires.length === 0) return { key: "MC_TPL_SAVE_ERR" }
      return {
        key: "MC_ORG_REG_ERR_DEPENDENCY_MISSING",
        values: { name, requires: requires.map(labelOf).join(", ") },
      }
    }
    case "DUPLICATE_REGULATION":
      return { key: "MC_ORG_REG_ERR_DUPLICATE", values: { name } }
    default:
      return { key: "MC_TPL_SAVE_ERR" }
  }
}

/** A line under a regulation's name about how it is linked to another. */
function CouplingNote({ children }: { children: ReactNode }) {
  return (
    <Typography as="muted" className="flex items-center gap-1.5">
      <Link2 className="size-3.5 shrink-0" />
      {children}
    </Typography>
  )
}

function RegulationRow({
  row,
  requiredBy,
  selected,
  canEdit,
  onToggle,
}: {
  row: RegulationSettingRow
  /** Rows whose `requires` names this one: switching it off drops them. */
  requiredBy: RegulationSettingRow[]
  selected: SelectionEntry | undefined
  canEdit: boolean
  onToggle: (checked: boolean) => void
}) {
  const { translate } = useTranslation()
  const label = regulationLabel(row.frameworkCode, row.name, translate)
  // `requires` carries raw regulation NAMES (e.g. "REACH_ANNEX_XVII"), not a
  // server-normalized frameworkCode: normalize first, like any raw string.
  const requiresLabels = row.requires.map((name) =>
    regulationLabel(normalizeFrameworkCode(name), name, translate),
  )
  const requiredByLabels = requiredBy.map((r) =>
    regulationLabel(r.frameworkCode, r.name, translate),
  )
  const active = !!selected
  const unsupported = row.notPickableReason === "not_supported"
  const hasDescription =
    row.requires.length > 0 || requiredBy.length > 0 || !row.pickable

  return (
    <SettingsListRow
      label={label}
      dimmed={!row.pickable}
      badges={
        row.pickable &&
        row.source === "inferred" && (
          <Badge variant="outline">
            {translate("MC_ORG_REG_BADGE_INFERRED")}
          </Badge>
        )
      }
      description={
        hasDescription && (
          <>
            {row.requires.length > 0 && (
              <CouplingNote>
                {translate("MC_ORG_REG_COUPLED_HINT", {
                  names: requiresLabels.join(", "),
                })}
              </CouplingNote>
            )}
            {requiredBy.length > 0 && (
              <CouplingNote>
                {translate("MC_ORG_REG_REQUIRED_BY_HINT", {
                  names: requiredByLabels.join(", "),
                })}
              </CouplingNote>
            )}
            {!row.pickable && (
              <Typography as="muted">
                {translate(
                  unsupported
                    ? "MC_ORG_REG_NOT_SUPPORTED"
                    : "MC_ORG_REG_UNAVAILABLE",
                )}
              </Typography>
            )}
          </>
        )
      }
      cells={[
        <SettingsSwitch
          key="active"
          checked={active}
          // A selected row can always be switched off, pickable or not: the
          // PUT is a full replace, so staying on would pin it in every save.
          disabled={!canEdit || (!row.pickable && !active)}
          label={label}
          tooltip={
            !row.pickable && !unsupported
              ? translate(unavailableTooltipKey(row.notPickableReason))
              : null
          }
          onCheckedChange={onToggle}
        />,
      ]}
    />
  )
}

/** The regulation switches, saved together. Mounted once the regulations
 *  have loaded, so the selection starts from them and a refetch after the
 *  save never resets an edit in progress. */
function RegulationsEditor({ rows }: { rows: RegulationSettingRow[] }) {
  const { translate } = useTranslation()
  const save = useSaveRegulationSettings()
  // Each regulation's reason (customer requirement, legal obligation, ...) is
  // stored and audited but read by nothing yet, so it isn't asked here: a save
  // sends the stored one, a new regulation the default.
  // A stored regulation whose prerequisite isn't stored yet gets it here.
  const [selection, setSelection] = useState<SelectionMap>(() =>
    withRequiredRegulations(rows, initialRegulationSelection(rows)),
  )
  const canEdit = save.canRun

  // An org running on inferred regulations may save the preselection as is:
  // that first save turns the inferred set into explicit rows.
  const hasInferred = rows.some((r) => r.source === "inferred")
  const changed =
    selectionFingerprint(selection) !==
    selectionFingerprint(initialRegulationSelection(rows))
  useUnsavedChanges(changed && canEdit)
  const activeCount = Object.keys(selection).length
  // An empty set is never saved: with no explicit rows the backend falls back
  // to the inferred regulations, so "none" can't be stored.
  const empty = activeCount === 0
  const pickableCount = rows.filter((r) => r.pickable).length

  const groups = REGION_ORDER.map((region) => ({
    region,
    rows: rows.filter((r) => regionOf(r) === region),
  })).filter((group) => group.rows.length > 0)

  function onToggle(row: RegulationSettingRow, checked: boolean) {
    setSelection((prev) => toggleRegulationSelection(rows, prev, row, checked))
  }

  function onSave() {
    const subscriptions: RegulationSubscriptionIn[] = Object.entries(
      selection,
    ).map(([regulationId, entry]) => ({
      regulationId,
      reason: entry.reason,
      ...(entry.requiredSince ? { requiredSince: entry.requiredSince } : {}),
    }))
    save.mutate(subscriptions, {
      onSuccess: () => toast.success(translate("MC_ORG_REG_SAVE_TOAST")),
      onError: (error) => {
        const { key, values } = regulationSaveError(
          error,
          rows,
          selection,
          translate,
        )
        toastApiError(error, translate, key, values)
      },
    })
  }

  return (
    <SettingsSection
      target="regulations"
      footer={
        <>
          {canEdit && (
            <PendingButton
              pending={save.isPending}
              disabled={(!hasInferred && !changed) || empty}
              onClick={onSave}
            >
              {translate("MC_TPL_SAVE")}
            </PendingButton>
          )}
          <Typography as="muted">
            {empty
              ? translate("MC_ORG_REG_EMPTY_HINT")
              : changed
                ? translate("MC_ORG_REG_UNSAVED")
                : translate("MC_ORG_REG_ACTIVE_COUNT", {
                    count: String(activeCount),
                    total: String(pickableCount),
                  })}
          </Typography>
        </>
      }
    >
      <SettingsList
        columns={[
          {
            label: translate("MC_ORG_REG_COL_ACTIVE"),
            className: "w-16 justify-center",
          },
        ]}
      >
        {groups.map((group) => (
          <SettingsListGroup
            key={group.region}
            title={translate(REGION_LABEL_KEY[group.region])}
          >
            {group.rows.map((row) => (
              <RegulationRow
                key={row.regulationId ?? row.name}
                row={row}
                requiredBy={rows.filter((r) => r.requires.includes(row.name))}
                selected={
                  row.regulationId ? selection[row.regulationId] : undefined
                }
                canEdit={canEdit}
                onToggle={(checked) => onToggle(row, checked)}
              />
            ))}
          </SettingsListGroup>
        ))}
      </SettingsList>
    </SettingsSection>
  )
}

/** Regelwerke: the regulations the org issues declarations of conformity
 *  for (mc_org_regulations). */
export function RegulationsPage() {
  const { translate } = useTranslation()
  const { data, isLoading, isError, isFetching, refetch } =
    useRegulationSettings()
  const rows = data?.regulations

  return (
    <SettingsPage
      title={translate("SETTINGS/REGULATIONS/TITLE")}
      description={translate("MC_ORG_REG_DESC")}
    >
      {rows ? (
        <RegulationsEditor rows={rows} />
      ) : (
        <SettingsSection>
          {isLoading && !isError ? (
            <SettingsSkeleton rows={5} />
          ) : (
            <SettingsLoadError
              pending={isFetching}
              onRetry={() => void refetch()}
            />
          )}
        </SettingsSection>
      )}
    </SettingsPage>
  )
}
